<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import paymentService from '../services/payment.service'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t, locale } = useI18n()

const clubUuid = route.params.uuid as string
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Filters
const paymentName = ref('')
const dateFrom = ref('')
const dateTo = ref('')

// Stats data
const currenciesData = ref<any[]>([])
const loading = ref(false)

// Update URL with query params
const updateUrl = () => {
  if (!dateFrom.value || !dateTo.value) return

  const query: Record<string, string> = {
    dateFrom: dateFrom.value,
    dateTo: dateTo.value
  }
  if (paymentName.value) {
    query.paymentName = paymentName.value
  }

  router.push({ path: route.path, query })
}

// Fetch financial stats
const fetchStats = async () => {
  if (!clubUuid || !dateFrom.value || !dateTo.value) {
    console.log('Missing required data:', { clubUuid, dateFrom: dateFrom.value, dateTo: dateTo.value })
    return
  }

  console.log('Fetching financial stats:', { clubUuid, dateFrom: dateFrom.value, dateTo: dateTo.value })

  // Update URL
  updateUrl()

  loading.value = true
  try {
    const response = await paymentService.getFinancialStats(clubUuid, dateFrom.value, dateTo.value, paymentName.value || undefined)
    console.log('Financial stats response:', response)
    currenciesData.value = response.currencies || []
    console.log('Currencies data:', currenciesData.value)
  } catch (err: any) {
    console.error('Error fetching financial stats:', err)
    console.error('Error response:', err.response?.data)
  } finally {
    loading.value = false
  }
}

// Initialize filters from URL or defaults
const initializeDates = () => {
  if (route.query.paymentName) {
    paymentName.value = route.query.paymentName as string
  }
  if (route.query.dateFrom && route.query.dateTo) {
    // Use dates from URL
    dateFrom.value = route.query.dateFrom as string
    dateTo.value = route.query.dateTo as string
  } else {
    // Default to last 6 months to next month (to include future payments)
    const today = new Date()
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(today.getMonth() - 6)

    const oneMonthAhead = new Date()
    oneMonthAhead.setMonth(today.getMonth() + 1)

    dateFrom.value = sixMonthsAgo.toISOString().split('T')[0]
    dateTo.value = oneMonthAhead.toISOString().split('T')[0]
  }
}

// Watch for filter changes and auto-fetch (with debounce to avoid double calls)
let fetchTimeout: ReturnType<typeof setTimeout> | null = null
watch([dateFrom, dateTo, paymentName], () => {
  if (fetchTimeout) {
    clearTimeout(fetchTimeout)
  }
  if (dateFrom.value && dateTo.value) {
    fetchTimeout = setTimeout(() => {
      fetchStats()
    }, 500)
  }
})

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat(locale.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

// Format "YYYY-MM" period using current locale
const formatPeriod = (period: string) => {
  const [year, month] = period.split('-')
  const date = new Date(Number(year), Number(month) - 1, 1)
  return new Intl.DateTimeFormat(locale.value, { month: 'long', year: 'numeric' }).format(date)
}

onMounted(() => {
  initializeDates()
  fetchStats()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="max-w-7xl mx-auto px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <button
          @click="router.push('/payments')"
          class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('common.backToPayments') }}
        </button>
      </div>

      <!-- Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('financialStats.title') }}</h1>
        <p class="text-sm text-gray-600 mt-1">{{ t('financialStats.subtitle') }}</p>
      </div>

      <!-- Filters -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-4 mb-4">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label for="paymentName" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('financialStats.paymentName') }}
            </label>
            <input
              type="text"
              id="paymentName"
              v-model="paymentName"
              :placeholder="t('financialStats.paymentNamePlaceholder')"
              class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
            />
          </div>
          <div>
            <label for="dateFrom" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('financialStats.dateFrom') }}
            </label>
            <input
              type="date"
              id="dateFrom"
              v-model="dateFrom"
              class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
            />
          </div>
          <div>
            <label for="dateTo" class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('financialStats.dateTo') }}
            </label>
            <input
              type="date"
              id="dateTo"
              v-model="dateTo"
              class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
            />
          </div>
          <div class="flex items-end">
            <button
              @click="fetchStats"
              :disabled="loading || !dateFrom || !dateTo"
              class="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ loading ? t('common.loading') : t('financialStats.refresh') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Tables (per currency) -->
      <div v-if="!loading && currenciesData.length > 0" class="space-y-6">
        <div v-for="currencyData in currenciesData" :key="currencyData.currency" class="bg-white border-2 border-gray-300 rounded shadow-sm overflow-hidden">
          <!-- Currency Header -->
          <div class="bg-gray-100 px-4 py-3 border-b-2 border-gray-300">
            <h2 class="text-lg font-semibold text-gray-900">
              {{ t('financialStats.currency') }}: {{ currencyData.currency }}
            </h2>
          </div>

          <!-- Summary Section -->
          <div class="bg-blue-50 px-4 py-4 border-b-2 border-gray-300">
            <h3 class="text-sm font-semibold text-gray-700 mb-3">{{ t('financialStats.summary') }}</h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div class="bg-white rounded-lg p-3 border-2 border-gray-200">
                <div class="text-xs text-gray-600 mb-1">{{ t('financialStats.toPay') }}</div>
                <div class="text-lg font-bold text-gray-900">
                  {{ formatCurrency(currencyData.totals.to_pay) }} {{ currencyData.currency }}
                </div>
                <div class="text-xs text-gray-400 mt-0.5">{{ t('financialStats.paymentsCount', { count: currencyData.totals.to_pay_count }) }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border-2 border-red-200">
                <div class="text-xs text-gray-600 mb-1">{{ t('financialStats.overdue') }}</div>
                <div class="text-lg font-bold text-red-600">
                  {{ formatCurrency(currencyData.totals.overdue) }} {{ currencyData.currency }}
                </div>
                <div class="text-xs text-gray-400 mt-0.5">{{ t('financialStats.paymentsCount', { count: currencyData.totals.overdue_count }) }}</div>
              </div>
              <div class="bg-white rounded-lg p-3 border-2 border-green-200">
                <div class="text-xs text-gray-600 mb-1">{{ t('financialStats.paid') }}</div>
                <div class="text-lg font-bold text-green-600">
                  {{ formatCurrency(currencyData.totals.paid) }} {{ currencyData.currency }}
                </div>
                <div class="text-xs text-gray-400 mt-0.5">{{ t('financialStats.paymentsCount', { count: currencyData.totals.paid_count }) }}</div>
              </div>
            </div>
          </div>

          <!-- Currency Table -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {{ t('financialStats.period') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {{ t('financialStats.toPay') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {{ t('financialStats.overdue') }}
                  </th>
                  <th class="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {{ t('financialStats.paid') }}
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="stat in currencyData.monthly_stats" :key="stat.period" class="hover:bg-gray-50">
                  <td class="px-4 py-3 text-sm font-medium text-gray-900">
                    {{ formatPeriod(stat.period) }}
                  </td>
                  <td class="px-4 py-3 text-sm text-right text-gray-900">
                    {{ formatCurrency(stat.to_pay) }} {{ currencyData.currency }}
                    <div class="text-xs text-gray-400">{{ t('financialStats.paymentsCount', { count: stat.to_pay_count }) }}</div>
                  </td>
                  <td class="px-4 py-3 text-sm text-right text-red-600 font-medium">
                    {{ formatCurrency(stat.overdue) }} {{ currencyData.currency }}
                    <div class="text-xs text-gray-400 font-normal">{{ t('financialStats.paymentsCount', { count: stat.overdue_count }) }}</div>
                  </td>
                  <td class="px-4 py-3 text-sm text-right text-green-600 font-medium">
                    {{ formatCurrency(stat.paid) }} {{ currencyData.currency }}
                    <div class="text-xs text-gray-400 font-normal">{{ t('financialStats.paymentsCount', { count: stat.paid_count }) }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-else-if="loading" class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-600">{{ t('common.loading') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm p-8 text-center">
        <p class="text-gray-600">{{ t('financialStats.noData') }}</p>
      </div>
    </div>
  </main>
</template>
